import { isDefined } from "@clipboard-health/util-ts";
import OpenInNewIcon from "@mui/icons-material/OpenInNew";
import { Dialog, DialogActions, DialogContent, DialogTitle, Stack } from "@mui/material";
import { useSession } from "@src/appV2/Auth/api/useSession";
import { SUPPORT_WEBFORM_URL } from "@src/appV2/ContactCBHInfo/constants";
import { useGetFacilityProfile } from "@src/appV2/FacilityProfile/api/useGetFacilityProfile";
import { CbhFeatureFlag, useCbhFlag } from "@src/appV2/FeatureFlags";
import { ExternalLink } from "@src/appV2/lib/Links/ExternalLink";
import { showErrorToast } from "@src/appV2/lib/Notifications";
import { useCreateWorkplaceAgreement } from "@src/appV2/WorkplaceAgreement/api/useCreateWorkplaceAgreement";
import { useGetWorkplaceAgreements } from "@src/appV2/WorkplaceAgreement/api/useGetWorkplaceAgreements";
import { LoadingButton, Text } from "@src/lib/ui-react";
import { isEmpty } from "lodash";
import { useState } from "react";

export function WorkplaceAgreementDialog() {
  const session = useSession();
  const { admin: isAdmin, profile, user } = session ?? { profile: undefined, user: undefined };
  const workplaceId = profile?.userId;
  const userId = user?._id ?? "";

  const { variation, flexAgreementUrl } = useCbhFlag(CbhFeatureFlag.REQUIRE_WORKPLACE_AGREEMENT, {
    defaultValue: {
      variation: "off",
      flexAgreementUrl: "",
    },
  });

  const requireWorkplaceAgreement =
    isDefined(workplaceId) && !isAdmin && !isEmpty(userId) && variation === "on";
  const {
    data: workplaceAgreements,
    isLoading: isLoadingWorkplaceAgreements,
    isError: isErrorGettingWorkplaceAgreements,
  } = useGetWorkplaceAgreements(workplaceId, undefined, {
    enabled: requireWorkplaceAgreement,
  });
  // Gate the dialog on the first shift post (populated async by the
  // ShiftCreated SQS consumer; shift-create mutations invalidate this query).
  const {
    data: facilityProfile,
    isLoading: isLoadingFacilityProfile,
    isError: isErrorGettingFacilityProfile,
  } = useGetFacilityProfile(workplaceId ?? "", "firstShiftCreatedAt", {
    enabled: requireWorkplaceAgreement,
  });
  const { mutateAsync: createWorkplaceAgreement, isLoading: isLoadingCreateWorkplaceAgreement } =
    useCreateWorkplaceAgreement(workplaceId);

  const [visible, setVisible] = useState<boolean>(true);
  const onAgree = async () => {
    try {
      await createWorkplaceAgreement({
        workplaceId,
        acceptedByUserId: userId,
        acceptedAt: new Date(),
        arbitrationAgreementVersionNumber: 1,
      });
      setVisible(false);
    } catch {
      showErrorToast("Failed to accept agreement. Please try again or contact support.");
    }
  };

  if (!requireWorkplaceAgreement) {
    return null;
  }

  if (
    isErrorGettingWorkplaceAgreements ||
    isLoadingWorkplaceAgreements ||
    !isEmpty(workplaceAgreements?.data)
  ) {
    return null;
  }

  if (
    isErrorGettingFacilityProfile ||
    isLoadingFacilityProfile ||
    !isDefined(facilityProfile?.firstShiftCreatedAt)
  ) {
    return null;
  }

  return (
    <Dialog disableEscapeKeyDown fullWidth open={visible} maxWidth="md">
      <DialogTitle sx={{ textAlign: "center" }}>Marketplace Access Agreements</DialogTitle>
      <DialogContent>
        <Stack spacing={2} alignItems="center" textAlign="center">
          <Text variant="body1">
            Please carefully read our{" "}
            <ExternalLink
              to="https://www.clipboardhealth.com/terms-of-service?menu=none"
              underline="always"
              sx={{ display: "inline-flex", alignItems: "center", gap: 0.25 }}
            >
              Terms of Service
              <OpenInNewIcon sx={{ fontSize: 14 }} />
            </ExternalLink>{" "}
            and Marketplace Access Agreements. By clicking &apos;AGREE AND ACCEPT,&apos; you
            represent and warrant that you have the authority to bind the organization associated
            with this account, and that you have read, understood, and agree to be bound by the
            Terms of Service and Marketplace Access Agreements listed below.
          </Text>
          <Stack spacing={1} alignItems="center" textAlign="center">
            <ExternalLink
              to={flexAgreementUrl}
              underline="always"
              sx={{ display: "inline-flex", alignItems: "center", gap: 0.25 }}
            >
              Flex Marketplace Access Agreement
              <OpenInNewIcon sx={{ fontSize: 14 }} />
            </ExternalLink>
          </Stack>
        </Stack>
      </DialogContent>
      <DialogActions sx={{ justifyContent: "center", pb: 3 }}>
        <Stack spacing={1} alignItems="center" textAlign="center">
          <LoadingButton
            variant="contained"
            size="large"
            sx={{ textTransform: "uppercase", px: 4, borderRadius: 6 }}
            disabled={isLoadingCreateWorkplaceAgreement}
            isLoading={isLoadingCreateWorkplaceAgreement}
            onClick={onAgree}
          >
            AGREE AND ACCEPT
          </LoadingButton>
          <Text variant="body2">
            <ExternalLink to={SUPPORT_WEBFORM_URL} underline="hover">
              Contact Support
            </ExternalLink>
          </Text>
        </Stack>
      </DialogActions>
    </Dialog>
  );
}
